import {
  Button,
  ButtonGroup,
  closeCurrentDialog,
  DataStatus,
  Form,
  FormButton,
  FormControl,
  FormField,
  FormSubmitHandler,
  showNotification,
} from 'platform/components';
import {Grid, Space} from 'platform/foundation';

import {useMemo} from 'react';

import {isNil} from 'ramda';

import {
  useCreateAccountingMatrixDatagridRecordMutation,
  useGetAccountingMatrixDatagridRecordQuery,
  useUpdateAccountingMatrixDatagridRecordMutation,
} from '@dms/api/finance';
import {useGetDictionaryQuery} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {handleApiError, refreshDmsDatagrid} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {
  AccountingMatrixDatagridCodelistCategory,
  AccountingMatrixDatagridConfig,
  AccountingMatrixDatagridFieldConfig,
  getAccountingMatrixDatagridGridCode,
} from '../utils/accountingMatrixDatagridConfig';

interface AccountingMatrixDatagridFormProps extends RequiredTestIdProps {
  config: AccountingMatrixDatagridConfig;
  /** Omit to create a new record; pass the eag-grid row id to edit an existing one. */
  recordId?: string;
}

const usesCodelist = (
  fields: AccountingMatrixDatagridConfig['fields'],
  category: AccountingMatrixDatagridCodelistCategory
) => fields.some((field) => field.type === 'choice' && field.codelistCategory === category);

export function AccountingMatrixDatagridForm(props: AccountingMatrixDatagridFormProps) {
  const isCreating = isNil(props.recordId);
  const gridCode = getAccountingMatrixDatagridGridCode(props.config.dgCode);

  const {
    data: record,
    isLoading: isRecordLoading,
    isError: isRecordError,
  } = useGetAccountingMatrixDatagridRecordQuery(
    {dgCode: props.config.dgCode, id: props.recordId ?? ''},
    {skip: isCreating}
  );

  const [createRecord, {isLoading: isCreatingRecord}] =
    useCreateAccountingMatrixDatagridRecordMutation();
  const [updateRecord, {isLoading: isUpdatingRecord}] =
    useUpdateAccountingMatrixDatagridRecordMutation();

  const {data: applicationModuleOptions} = useGetDictionaryQuery(
    {category: 'ACCapplicationModule'},
    {skip: !usesCodelist(props.config.fields, 'ACCapplicationModule')}
  );
  const {data: operationAreaOptions} = useGetDictionaryQuery(
    {category: 'ACCoperationArea'},
    {skip: !usesCodelist(props.config.fields, 'ACCoperationArea')}
  );
  const {data: itemGroupsOptions} = useGetDictionaryQuery(
    {category: 'ACCitemGroups'},
    {skip: !usesCodelist(props.config.fields, 'ACCitemGroups')}
  );

  const codelistOptionsByCategory: Record<
    AccountingMatrixDatagridCodelistCategory,
    typeof applicationModuleOptions
  > = {
    ACCapplicationModule: applicationModuleOptions,
    ACCoperationArea: operationAreaOptions,
    ACCitemGroups: itemGroupsOptions,
  };

  // Some fields the API nests (e.g. application: {code, translation}, billingInformation:
  // {id, cin, name}) while the form only ever holds their flat value — see fromRecord/toBody
  // on AccountingMatrixDatagridFieldConfig.
  const defaultValues = useMemo(() => {
    if (!record) {
      return undefined;
    }
    const values: Record<string, unknown> = {};
    props.config.fields.forEach((field) => {
      values[field.name] = field.fromRecord ? field.fromRecord(record) : record[field.name];
    });
    return values;
  }, [record, props.config.fields]);

  // Fields hidden by `showWhen` (e.g. M202/M205's workshop-only fields) stay registered by
  // react-hook-form even after unmounting, so their stale value must be discarded here rather
  // than relied on to be cleared by the form itself.
  const buildRequestBody = (data: Record<string, unknown>) => {
    const body: Record<string, unknown> = {};
    props.config.fields.forEach((field) => {
      const isVisible = !field.showWhen || field.showWhen(data);
      const rawValue = isVisible ? data[field.name] : null;
      body[field.name] = field.toBody ? field.toBody(rawValue) : rawValue;
    });
    return body;
  };

  const handleSubmit: FormSubmitHandler<Record<string, unknown>> = async (data) => {
    const body = buildRequestBody(data);

    if (isCreating) {
      await createRecord({dgCode: props.config.dgCode, body})
        .unwrap()
        .then(() => {
          showNotification.success(
            i18n.t('entity.accounting.notifications.accountingMatrixDatagridRecordCreated')
          );
          refreshDmsDatagrid(gridCode);
          closeCurrentDialog();
        })
        .catch(handleApiError);
    } else if (props.recordId) {
      await updateRecord({dgCode: props.config.dgCode, id: props.recordId, body})
        .unwrap()
        .then(() => {
          showNotification.success(
            i18n.t('entity.accounting.notifications.accountingMatrixDatagridRecordUpdated')
          );
          refreshDmsDatagrid(gridCode);
          closeCurrentDialog();
        })
        .catch(handleApiError);
    }
  };

  // `field.type` stays the AccountingMatrixDatagridFieldType union here (not narrowed to a single
  // literal branch) so FormField's per-type `name` constraint resolves permissively across the
  // union instead of requiring a per-DG literal field-values type — matches the existing
  // config-driven FormField dispatch in PlatformSettingsPromptBridge.
  const renderField = (
    field: AccountingMatrixDatagridFieldConfig,
    control: FormControl<Record<string, unknown>>
  ) => (
    <FormField
      key={field.name}
      type={field.type}
      control={control}
      name={field.name}
      isRequired={field.isRequired}
      label={field.label}
      options={
        field.type === 'choice' && field.codelistCategory
          ? (codelistOptionsByCategory[field.codelistCategory] ?? [])
          : undefined
      }
      data-testid={suffixTestId(field.name, props)}
    />
  );

  return (
    <DataStatus isLoading={isRecordLoading} isError={isRecordError}>
      <Form<Record<string, unknown>>
        experimentalZodSchema={props.config.schema}
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
      >
        {(control, formApi) => {
          const values = formApi.watch();
          const visibleFields = props.config.fields.filter(
            (field) => !field.showWhen || field.showWhen(values)
          );

          return (
            <>
              <Grid columns={2}>{visibleFields.map((field) => renderField(field, control))}</Grid>

              <Space vertical={4} />
              <ButtonGroup align="right">
                <Button
                  title={i18n.t('general.actions.discard')}
                  onClick={closeCurrentDialog}
                  variant="secondary"
                  data-testid={suffixTestId('discard', props)}
                />
                <FormButton
                  title={
                    isCreating ? i18n.t('general.actions.create') : i18n.t('general.actions.save')
                  }
                  control={control}
                  type="submit"
                  variant="primary"
                  isLoading={isCreatingRecord || isUpdatingRecord}
                  data-testid={suffixTestId('submit', props)}
                />
              </ButtonGroup>
            </>
          );
        }}
      </Form>
    </DataStatus>
  );
}
